import React, { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { fieldRecommendationsApi } from '../services/fieldRecommendationsApi';
import '../styles/FieldRecommendations.css';

export const isRecommendationTargetBlank = value => value === null || value === undefined
    || (typeof value === 'string' && value.trim() === '');

const positiveId = value => {
    const text = String(value ?? '');
    const number = Number(text);
    return /^[1-9]\d*$/.test(text) && Number.isSafeInteger(number) ? number : null;
};

// Add-item group indexes start at zero; null is reserved for a new draft.
const groupIndex = value => {
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const text = String(value);
    const number = Number(text);
    return /^(0|[1-9]\d*)$/.test(text) && Number.isSafeInteger(number)
        && number <= 2147483647 ? number : null;
};

const errorMessage = error => error?.status === 409
    ? 'This recommendation changed. Refresh it before applying.'
    : error?.message || 'Recommendations could not be checked. Your draft has not changed.';

const recommendationKey = item => {
    const candidate = item.recommendation;
    // Renewing an expiring server token must not undo a user's dismissal.
    return candidate ? JSON.stringify([
        'suggestion', candidate.rule_id, candidate.rule_version, candidate.target_field_id,
        candidate.target_contact_field_id, candidate.contact_id, candidate.target_field_type,
        candidate.mapping_mode, candidate.value, candidate.label,
    ]) : `conflict:${item.conflict}`;
};

const validateRecommendation = (recommendation, inputs) => {
    const targetId = positiveId(recommendation?.target_field_id);
    const contactFieldId = positiveId(recommendation?.target_contact_field_id);
    const contactId = positiveId(recommendation?.contact_id);
    const targetType = Number(recommendation?.target_field_type);
    const localTargetType = inputs.targets.get(String(targetId));
    const value = recommendation?.value;
    const valueIsValid = targetType === 8
        ? positiveId(value) !== null && recommendation?.mapping_mode === 'direct_contact'
        : targetType === 10
            ? Number.isSafeInteger(value) && value >= 0 && recommendation?.mapping_mode === 'mapped_dropdown'
            : false;
    if (!targetId || !localTargetType || localTargetType !== targetType
        || !isRecommendationTargetBlank(inputs.values[targetId])
        || !contactFieldId || !contactId || inputs.contacts[contactFieldId] !== contactId
        || !positiveId(recommendation?.rule_id)
        || !positiveId(recommendation?.rule_version)
        || typeof recommendation?.token !== 'string' || !recommendation.token
        || !valueIsValid
        || typeof recommendation?.label !== 'string') return null;
    return { ...recommendation, target_field_id: targetId, target_field_type: targetType };
};

/** Recommendations are previews only. Accepting changes the current draft;
 * the Section's existing Save action remains the only persistence path. */
export const useFieldRecommendations = ({
    caseId, sectionId, groupId = null, draftKey, userId, fields = [],
    fieldValues = {}, enabled = false, onAccept,
}) => {
    const relevantFields = fields.filter(field => [8, 10].includes(Number(field?.field_id)))
        .filter(field => positiveId(field?.id)).sort((a, b) => Number(a.id) - Number(b.id));
    const contacts = {};
    const targets = new Map();
    relevantFields.forEach(field => {
        const fieldType = Number(field.field_id);
        targets.set(String(field.id), fieldType);
        if (fieldType === 8) {
            const value = fieldValues[field.id];
            const contactId = positiveId(value && typeof value === 'object' ? value.id : value);
            if (contactId) contacts[field.id] = contactId;
        }
    });
    const payload = {
        case_id: positiveId(caseId), section_id: positiveId(sectionId),
        group_id: groupId === null || groupId === undefined ? null : groupIndex(groupId),
        contact_values: contacts,
    };
    const canPreview = enabled && positiveId(userId) && payload.case_id && payload.section_id
        && (groupId === null || groupId === undefined || payload.group_id !== null)
        && Object.keys(contacts).length > 0
        && [...targets.keys()].some(id => isRecommendationTargetBlank(fieldValues[id]));
    const scopeKey = JSON.stringify([
        !!canPreview, payload, userId, draftKey,
        relevantFields.map(field => [field.id, field.field_id, field.options, fieldValues[field.id]]),
    ]);
    const scopeRef = useRef({ key: scopeKey });
    if (scopeRef.current.key !== scopeKey) scopeRef.current = { key: scopeKey };
    const inputsRef = useRef(null);
    inputsRef.current = { scope: scopeRef.current, payload, contacts, targets, values: fieldValues, onAccept, canPreview };
    const previewGeneration = useRef(0);
    const acceptsRef = useRef(new Map());
    const [refresh, setRefresh] = useState(0);
    const [state, setState] = useState({ scope: null, items: {}, loading: false, error: '' });
    const [dismissals, setDismissals] = useState({ scope: null, keys: new Set() });

    const cancelAccepts = () => {
        acceptsRef.current.forEach(request => request.controller.abort());
        acceptsRef.current.clear();
    };

    useEffect(() => {
        const inputs = inputsRef.current;
        const scope = inputs.scope;
        const generation = ++previewGeneration.current;
        const controller = new AbortController();
        cancelAccepts();
        setState({ scope, items: {}, loading: !!inputs.canPreview, error: '' });
        if (inputs.canPreview) {
            fieldRecommendationsApi.preview(inputs.payload, { signal: controller.signal }).then(result => {
                if (controller.signal.aborted || scopeRef.current !== scope || previewGeneration.current !== generation) return;
                const items = {};
                for (const candidate of Array.isArray(result?.recommendations) ? result.recommendations : []) {
                    const recommendation = validateRecommendation(candidate, inputsRef.current);
                    if (!recommendation) continue;
                    const id = String(recommendation.target_field_id);
                    const previous = items[id]?.recommendation;
                    items[id] = previous && (previous.value !== recommendation.value || previous.label !== recommendation.label)
                        ? { conflict: 'Matching records disagree. Review the source records before choosing a value.' }
                        : items[id]?.conflict ? items[id] : { recommendation };
                }
                for (const conflict of Array.isArray(result?.conflicts) ? result.conflicts : []) {
                    const id = String(positiveId(conflict?.target_field_id) || '');
                    if (inputs.targets.has(id) && isRecommendationTargetBlank(inputs.values[id])) {
                        items[id] = { conflict: typeof conflict.message === 'string' ? conflict.message
                            : 'Matching records disagree. Review the source records before choosing a value.' };
                    }
                }
                setState({ scope, items, loading: false, error: '' });
            }).catch(error => {
                if (controller.signal.aborted || scopeRef.current !== scope || previewGeneration.current !== generation) return;
                setState({ scope, items: {}, loading: false,
                    error: [404, 503].includes(error?.status) ? '' : errorMessage(error) });
            });
        }
        return () => { controller.abort(); cancelAccepts(); };
    }, [scopeKey, refresh]);

    const currentState = state.scope === scopeRef.current ? state : { items: {}, loading: !!canPreview, error: '' };
    const byField = Object.fromEntries(Object.entries(currentState.items).filter(([, item]) => (
        dismissals.scope !== scopeRef.current || !dismissals.keys.has(recommendationKey(item))
    )));
    const visibleRef = useRef(byField);
    visibleRef.current = byField;

    const dismiss = fieldId => {
        const id = String(fieldId);
        const item = visibleRef.current[id];
        if (!item) return;
        acceptsRef.current.get(id)?.controller.abort();
        acceptsRef.current.delete(id);
        const scope = scopeRef.current;
        setDismissals(previous => ({
            scope,
            keys: new Set([...(previous.scope === scope ? previous.keys : []), recommendationKey(item)]),
        }));
    };

    const accept = async fieldId => {
        const id = String(fieldId);
        const inputs = inputsRef.current;
        const item = visibleRef.current[id];
        if (!inputs.canPreview || item?.conflict || acceptsRef.current.has(id)) return false;
        const recommendation = validateRecommendation(item?.recommendation, inputs);
        if (!recommendation) return false;
        const request = { scope: inputs.scope, controller: new AbortController() };
        acceptsRef.current.set(id, request);
        const isCurrent = () => scopeRef.current === request.scope
            && acceptsRef.current.get(id) === request && !request.controller.signal.aborted;
        setState(previous => previous.scope !== request.scope ? previous : {
            ...previous, items: { ...previous.items, [id]: { ...previous.items[id], accepting: true, error: '' } },
        });
        try {
            const result = await fieldRecommendationsApi.accept({
                ...inputs.payload, rule_id: recommendation.rule_id, token: recommendation.token,
            }, { signal: request.controller.signal });
            if (!isCurrent()) return false;
            const fresh = validateRecommendation(result?.recommendation, inputsRef.current);
            if (!fresh || fresh.target_field_id !== recommendation.target_field_id
                || String(fresh.target_contact_field_id) !== String(recommendation.target_contact_field_id)
                || String(fresh.contact_id) !== String(recommendation.contact_id)
                || String(fresh.rule_id) !== String(recommendation.rule_id)
                || String(fresh.rule_version) !== String(recommendation.rule_version)
                || fresh.value !== recommendation.value || fresh.label !== recommendation.label) {
                throw Object.assign(new Error('The recommendation changed.'), { status: 409 });
            }
            await inputsRef.current.onAccept?.(fresh.target_field_id, fresh.value);
            return true;
        } catch (error) {
            if (isCurrent()) setState(previous => previous.scope !== request.scope ? previous : {
                ...previous, items: { ...previous.items, [id]: { ...previous.items[id], error: errorMessage(error) } },
            });
            return false;
        } finally {
            if (isCurrent()) {
                acceptsRef.current.delete(id);
                setState(previous => previous.scope !== request.scope ? previous : {
                    ...previous, items: { ...previous.items, [id]: { ...previous.items[id], accepting: false } },
                });
            }
        }
    };

    const retry = () => {
        previewGeneration.current += 1;
        cancelAccepts();
        setRefresh(value => value + 1);
    };
    return {
        byField,
        loading: currentState.loading,
        accepting: Object.values(byField).some(item => !!item.accepting),
        error: currentState.error,
        retry,
        accept,
        dismiss,
    };
};

export const FieldRecommendation = ({ item, fieldName = 'this field', onAccept, onDismiss, onRetry }) => {
    if (!item) return null;
    const recommendation = item.recommendation;
    return (
        <div className="field-recommendation" role="group" aria-label={`${fieldName} recommendation`}>
            <div className="field-recommendation-controls">
                {!item.conflict && recommendation && (
                    <button type="button" className="action-micro quaternary field-recommendation-control"
                        disabled={!!item.accepting} aria-busy={!!item.accepting}
                        title={item.accepting ? 'Checking recommendation…' : `Use ${recommendation.label} for ${fieldName}`}
                        aria-label={`Use ${recommendation.label} for ${fieldName}`} onClick={onAccept}>
                        <Check size={16} aria-hidden="true" />
                    </button>
                )}
                <button type="button" className="action-micro quaternary field-recommendation-control"
                    title={`Dismiss recommendation for ${fieldName}`} aria-label={`Dismiss recommendation for ${fieldName}`} onClick={onDismiss}>
                    <X size={16} aria-hidden="true" />
                </button>
            </div>
            {(item.conflict || item.error) && <div className="field-recommendation-feedback">
                {item.conflict && <span className="field-recommendation-conflict" role="status">{item.conflict}</span>}
                {item.error && <span className="field-recommendation-error" role="alert">{item.error}</span>}
                {onRetry && (
                    <button type="button" className="action-tiny quaternary" onClick={onRetry}>Refresh recommendation</button>
                )}
            </div>}
        </div>
    );
};
